import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { useAuth } from '../../auth/AuthContext';
import { authReturnLocation } from '../../auth/authReturn';
import { travelSafetyApi } from '../../api/travelSafetyApi';
import MemberReportDialog from './MemberReportDialog';
import {
  canShowMemberSafetyActions,
  newSafetyClientRequestId,
  safetyErrorMessage,
} from './memberSafetyModel';

/**
 * Lightweight Report / Block actions against a MEMBER (AUTH-28).
 * Does not mutate GroupTrip / Trade / Payment.
 */
export default function MemberSafetyActions({
  targetMemberId,
  reportTitleKey = 'travel.safety.reportMember',
  reportLabelKey = 'travel.safety.report',
  blockLabelKey = 'travel.safety.block',
  onBlocked,
}) {
  const { t } = useTravelTranslation();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const actorId = user?.memberId ?? user?.id;
  const [reportOpen, setReportOpen] = useState(false);
  const [confirmBlock, setConfirmBlock] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [blockedLocally, setBlockedLocally] = useState(false);
  const blockRequestIdRef = useRef(null);

  if (!canShowMemberSafetyActions({ actorMemberId: actorId, targetMemberId })) {
    return null;
  }

  function requireAuth() {
    if (isAuthenticated) return true;
    navigate('/login', { state: { from: authReturnLocation(location) } });
    return false;
  }

  function openReport() {
    if (!requireAuth()) return;
    setError('');
    setMessage('');
    setReportOpen(true);
  }

  function openBlockConfirm() {
    if (!requireAuth()) return;
    setError('');
    setMessage('');
    blockRequestIdRef.current = newSafetyClientRequestId('block');
    setConfirmBlock(true);
  }

  async function confirmBlockAction() {
    if (busy || blockedLocally || targetMemberId == null) return;
    if (!blockRequestIdRef.current) {
      blockRequestIdRef.current = newSafetyClientRequestId('block');
    }
    setBusy(true);
    setError('');
    try {
      await travelSafetyApi.blockMember(targetMemberId, blockRequestIdRef.current);
      setBlockedLocally(true);
      setConfirmBlock(false);
      setMessage(t('travel.safety.memberBlocked'));
      onBlocked?.(targetMemberId);
    } catch (cause) {
      setError(safetyErrorMessage(cause, t, 'travel.safety.blockFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="member-safety-actions" data-testid="member-safety-actions">
      <div className="group-trips__actions member-safety-actions__row">
        <button type="button" className="member-safety-actions__secondary" onClick={openReport}>
          {t(reportLabelKey)}
        </button>
        <button
          type="button"
          className="member-safety-actions__secondary"
          disabled={blockedLocally || busy}
          onClick={openBlockConfirm}
        >
          {blockedLocally ? t('travel.safety.memberBlocked') : t(blockLabelKey)}
        </button>
      </div>
      {message ? <p className="group-trips__hint">{message}</p> : null}
      {error ? <p className="group-trips__error" role="alert">{error}</p> : null}
      {confirmBlock ? (
        <div className="member-safety-confirm" role="alertdialog" aria-labelledby="block-confirm-title">
          <p id="block-confirm-title">{t('travel.safety.blockConfirm')}</p>
          <div className="group-trips__actions">
            <button type="button" disabled={busy} onClick={confirmBlockAction}>
              {busy ? t('travel.safety.blocking') : t('travel.safety.confirmBlock')}
            </button>
            <button type="button" disabled={busy} onClick={() => setConfirmBlock(false)}>
              {t('travel.safety.cancel')}
            </button>
          </div>
        </div>
      ) : null}
      <MemberReportDialog
        open={reportOpen}
        targetMemberId={targetMemberId}
        titleKey={reportTitleKey}
        onClose={() => setReportOpen(false)}
      />
    </div>
  );
}
